import { Component, Input, Output, EventEmitter, TemplateRef, ChangeDetectionStrategy, OnInit, inject } from '@angular/core';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, Params } from '@angular/router';

export interface TabConfig {
  id: string;
  labelKey: string;
  template?: TemplateRef<unknown>;
}

export interface TabsConfiguration {
  tabs: TabConfig[];
  defaultTab: string;
  storageKey: string;
  enableRouting?: boolean;
  disableStorage?: boolean;
}

@Component({
  selector: 'app-reusable-tabs',
  templateUrl: './reusable-tabs.component.html',
  styleUrls: ['./reusable-tabs.component.scss'],
  imports: [
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    TranslatePipe,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReusableTabsComponent implements OnInit {
  private _tabConfig!: TabsConfiguration;

  @Input()
  set tabConfig(value: TabsConfiguration) {
    this._tabConfig = value;
    if (value && value.tabs && value.tabs.length > 0) {
      this.initializeTabs();
    }
  }

  get tabConfig(): TabsConfiguration {
    return this._tabConfig;
  }

  @Output() tabChange = new EventEmitter<string>();

  tabs: TabConfig[] = [];
  currentTab = '';
  private enableRouting = true;
  private disableStorage = false;
  private storageKey = '';
  private defaultTab = '';

  private route = inject(ActivatedRoute);
  private router = inject(Router);

  ngOnInit(): void {
    if (this.tabConfig && this.tabConfig.tabs && this.tabConfig.tabs.length > 0) {
      this.initializeTabs();
    }
  }

  private initializeTabs(): void {
    this.tabs = this.tabConfig.tabs;
    this.defaultTab = this.tabConfig.defaultTab;
    this.storageKey = this.tabConfig.storageKey;
    this.enableRouting = this.tabConfig.enableRouting ?? true;
    this.disableStorage = this.tabConfig.disableStorage ?? false;

    if (this.enableRouting) {
      this.initializeTabFromRouteOrStorage();
    } else {
      const savedTab = this.getFromStorage();
      this.currentTab = savedTab ?? this.defaultTab;
      if (this.currentTab) {
        this.tabChange.emit(this.currentTab);
      }
    }
  }

  onTabSelect(tabId: string | number | undefined): void {
    if (typeof tabId === 'string') {
      this.updateRouteAndStorage(tabId);
    }
  }

  private initializeTabFromRouteOrStorage(): void {
    const params = this.route.snapshot.queryParams;
    const queryTab = params['tab'];

    if (queryTab) {
      this.currentTab = queryTab;
      this.saveToStorage(queryTab);
    } else {
      const savedTab = this.getFromStorage();
      if (savedTab) {
        this.currentTab = savedTab;
        this.updateRoute(savedTab);
      } else {
        this.currentTab = this.defaultTab;
        this.updateRoute(this.defaultTab);
      }
    }
    if (this.currentTab) {
      this.tabChange.emit(this.currentTab);
    }
  }

  private updateRouteAndStorage(tabId: string): void {
    this.currentTab = tabId;
    this.saveToStorage(tabId);
    this.updateRoute(tabId);
    this.tabChange.emit(tabId);
  }

  private updateRoute(tab: string): void {
    const queryParams: Params = { tab };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private saveToStorage(tab: string): void {
    if (this.storageKey && !this.disableStorage) {
      localStorage.setItem(this.storageKey, tab);
    }
  }

  private getFromStorage(): string | null {
    if (this.storageKey && !this.disableStorage) {
      return localStorage.getItem(this.storageKey);
    }
    return null;
  }

  hasContent(tab: TabConfig): boolean {
    return !!tab.template;
  }

  getContent(tab: TabConfig): TemplateRef<unknown> | null {
    return tab.template || null;
  }
}